import { format } from 'date-fns';
import { toast } from 'sonner';
import { Check, X, CreditCard } from 'lucide-react';
import { useAdminPlanRequests, useApprovePlanRequest, useRejectPlanRequest } from '@/api/queries/superAdmin.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export default function SuperAdminProRequestsPage() {
  const { data, isLoading } = useAdminPlanRequests({ type: 'PRO_UPGRADE' });
  const { mutate: approve, isPending: approving } = useApprovePlanRequest();
  const { mutate: reject, isPending: rejecting } = useRejectPlanRequest();
  const requests = data?.data ?? [];

  const handleApprove = (id) => {
    approve(id, {
      onSuccess: () => toast.success('Pro plan approved — company unlocked'),
      onError: () => toast.error('Failed to approve request'),
    });
  };
  const handleReject = (id) => {
    reject({ id, reject_reason: 'Not eligible' }, {
      onSuccess: () => toast.success('Request rejected'),
      onError: () => toast.error('Failed to reject request'),
    });
  };

  return (
    <div className="space-y-5 fade-in max-w-5xl">
      <PageHeader
        title="Pro Plan Requests"
        subtitle="Approve once the dummy payment is confirmed — flips the company straight to Pro"
        breadcrumb={[{ label: 'Super Admin', href: '/super-admin' }, { label: 'Pro Requests' }]}
      />

      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <p className="text-[13px] text-[var(--text-secondary)] p-6 text-center">Loading…</p>
        ) : requests.length === 0 ? (
          <p className="text-[13px] text-[var(--text-secondary)] p-6 text-center">No Pro plan requests yet.</p>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-[var(--border)] text-left">
                <th className="px-5 py-3 font-semibold text-[var(--text-secondary)]">Company</th>
                <th className="px-5 py-3 font-semibold text-[var(--text-secondary)]">Payment</th>
                <th className="px-5 py-3 font-semibold text-[var(--text-secondary)]">Requested</th>
                <th className="px-5 py-3 font-semibold text-[var(--text-secondary)]">Status</th>
                <th className="px-5 py-3 font-semibold text-[var(--text-secondary)]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(r => (
                <tr key={r.id} className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg-hover)] transition-colors">
                  <td className="px-5 py-3 font-semibold text-[var(--text-primary)]">{r.contact_company_name || '—'}</td>
                  <td className="px-5 py-3">
                    {r.dummy_payment_completed ? (
                      <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                        <CreditCard size={13}/> ₹{((r.dummy_payment_amount ?? 0) / 100).toFixed(0)} paid (test)
                      </span>
                    ) : <span className="text-[var(--text-tertiary)]">No payment</span>}
                  </td>
                  <td className="px-5 py-3 text-[var(--text-tertiary)]">{r.created_at ? format(new Date(r.created_at), 'd MMM, h:mm a') : '—'}</td>
                  <td className="px-5 py-3">
                    <Badge variant={r.status === 'PENDING' ? 'yellow' : r.status === 'APPROVED' ? 'green' : 'red'}>{r.status}</Badge>
                  </td>
                  <td className="px-5 py-3">
                    {r.status === 'PENDING' ? (
                      <div className="flex gap-2">
                        <Button size="xs" variant="success" disabled={approving} onClick={() => handleApprove(r.id)}>
                          <Check size={12}/> Approve
                        </Button>
                        <Button size="xs" variant="danger" disabled={rejecting} onClick={() => handleReject(r.id)}>
                          <X size={12}/> Reject
                        </Button>
                      </div>
                    ) : <span className="text-[var(--text-tertiary)]">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
